import { useEffect, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Form,
  Grid,
  Input,
  message,
  Spin,
  Tooltip,
  Typography,
} from "antd";
import {
  UserOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import logoIcon from "../../assets/images/eko-favicon.png";
import { useLocation } from "react-router-dom";
import { getTenantInfoApi } from "../../services/tenant.service";
import { useAppDispatch } from "../../hooks/reduxHooks";
import { buildTenantLoginUrl } from "../../utils/tenantFromHost";
import TenantIdDialog from "./TenantIdDialog";
import TenantNotFoundPageComponent from "../tenantNotFoundPage/tenantNotFoundPage.component";
import ServerErrorPageComponent from "../serverErrorPage/serverErrorPage.component";
import { Building2 } from "lucide-react";

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const LoginComponent = (props) => {
  const {
    handleLogin,
    submitting,
    slug,
    tenantCandidate,
    showSignupSuccess,
    isSubdomainTenantRequest,
  } = props;
  const { executeRecaptcha } = useGoogleReCaptcha();
  const screens = useBreakpoint();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isMobile = !screens.md;
  const isTablet = !!screens.md && !screens.lg;

  const [initialLoading, setinitialLoading] = useState(true);
  const [tenantDialogVisible, setTenantDialogVisible] = useState(false);
  const [showSubdomainNotFound, setShowSubdomainNotFound] = useState(false);
  const [showSubdomainServerError, setShowSubdomainServerError] = useState(false);
  const [tenantDisplayName, setTenantDisplayName] = useState(
    typeof slug === "string" ? slug : ""
  );

  const onFinish = async (val) => {
    if (!executeRecaptcha) {
      message.error("reCAPTCHA not loaded. Please try again.");
      return;
    }

    try {
      const token = await executeRecaptcha('login');
      const dataWithToken = { ...val, recaptcha_token: token };
      handleLogin(dataWithToken);
    } catch (error) {
      console.error("reCAPTCHA execution failed:", error);
      message.error("Failed to verify reCAPTCHA. Please try again.");
    }
  };

  const handleTenantDialogClose = () => {
    setTenantDialogVisible(false);
  };

  useEffect(() => {
    const { source } = location.state || {};
    if (source === "expired") {
      message.warning("Session expired. Please log in again.");
    }
  }, []);

  useEffect(() => {
    if (showSignupSuccess) {
      message.success("Signup successful, now you can login to your account.");
    }
  }, [showSignupSuccess]);

  useEffect(() => {
    if (!slug) {
      if (isSubdomainTenantRequest) {
        setinitialLoading(false);
        setShowSubdomainNotFound(true);
        return;
      }

      const savedTenantSlug = localStorage.getItem("slug")?.trim().toLowerCase();
      const isValidSavedSlug =
        typeof savedTenantSlug === "string" &&
        /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(savedTenantSlug);

      if (isValidSavedSlug) {
        window.location.href = buildTenantLoginUrl({
          slug: savedTenantSlug,
          search: location.search || "",
        });
        return;
      }

      setinitialLoading(false);
      setTenantDialogVisible(true);
      return;
    }

    setTenantDisplayName(slug);

    (async () => {
      dispatch(
        getTenantInfoApi({
          params: {
            slug: slug,
          },
          finalCallback: () => setinitialLoading(false),
          successCallback: (data) => {
            const resolvedTenantName =
              data?.tenant_label || data?.tenant_name || data?.org_name || slug;
            setTenantDisplayName(resolvedTenantName);

            if (!data?.tenant_id) {
              if (isSubdomainTenantRequest) {
                setShowSubdomainNotFound(true);
                return;
              }

              message.error(
                "Tenant information not found. Please contact support."
              );
              setTenantDialogVisible(true);
            }
          },
          failureCallback: (error) => {
            const status = error?.response?.status;
            console.error("Error fetching tenant info:", error);

            if (status === 404) {
              if (isSubdomainTenantRequest) {
                setShowSubdomainNotFound(true);
                return;
              }

              setTenantDialogVisible(true);
            } else {
              if (isSubdomainTenantRequest) {
                setShowSubdomainServerError(true);
                return;
              }

              message.error(
                "Failed to fetch tenant information. Please try again later."
              );
              setTenantDialogVisible(true);
            }
          },
        })
      );
    })();
  }, [slug, dispatch, isSubdomainTenantRequest]);

  if (showSubdomainNotFound) {
    return (
      <TenantNotFoundPageComponent
        tenantName={tenantDisplayName || tenantCandidate || slug}
      />
    );
  }

  if (showSubdomainServerError) {
    return <ServerErrorPageComponent />;
  }

  return (
    <div style={styles.container(isMobile)}>
      <Spin spinning={initialLoading} tip="Logging in..." size="large">
        <Card style={styles.card(isMobile, isTablet)} bordered={false}>
          <div style={styles.cardContent(isMobile, isTablet)}>
            {/* Header Section */}
            <div style={styles.header(isMobile)}>
              <img src={logoIcon} alt="Company Logo" style={styles.logo(isMobile)} />
              <Title level={2} style={styles.title(isMobile)}>
                Welcome Back
              </Title>
              <Text style={styles.subtitle(isMobile)}>
                Please sign in to your account
              </Text>
              {tenantDisplayName && (
                <Tooltip title={`You are logging in to ${tenantDisplayName}`}>
                  <div style={styles.tenantRow(isMobile)}>
                    <Building2 style={styles.tenantIcon} />
                    <Text style={styles.tenantText(isMobile)}>{tenantDisplayName}</Text>
                  </div>
                </Tooltip>
              )}
            </div>

            {/* Login form */}
            <Form
              name="login_form"
              layout="vertical"
              onFinish={onFinish}
              style={styles.form}
              initialValues={{ keepSignedIn: true }}
            >
              <Form.Item
                name="username"
                label={<span style={styles.label(isMobile)}>Username</span>}
                rules={[
                  { required: true, message: "Please input your username!" },
                ]}
                style={styles.formItem}
              >
                <Input
                  size="large"
                  placeholder="Enter your username"
                  prefix={<UserOutlined style={styles.inputIcon} />}
                  style={styles.input(isMobile)}
                />
              </Form.Item>

              <Form.Item
                name="password"
                label={<span style={styles.label(isMobile)}>Password</span>}
                rules={[
                  { required: true, message: "Please input your password!" },
                ]}
                style={styles.formItem}
              >
                <Input.Password
                  size="large"
                  placeholder="Enter your password"
                  prefix={<LockOutlined style={styles.inputIcon} />}
                  style={styles.input(isMobile)}
                />
              </Form.Item>

              <Form.Item style={styles.submitFormItem}>
                <Form.Item
                  name="keepSignedIn"
                  valuePropName="checked"
                  style={styles.keepSignedInItem}
                >
                  <Checkbox style={styles.keepSignedInCheckbox(isMobile)}>
                    Keep me signed in
                  </Checkbox>
                </Form.Item>

                <Button
                  type="primary"
                  htmlType="submit"
                  loading={submitting}
                  block
                  size="large"
                  style={styles.button(isMobile)}
                >
                  Sign In
                </Button>
              </Form.Item>
            </Form>
          </div>
        </Card>
      </Spin>

      {/* Tenant ID Dialog */}
      {!isSubdomainTenantRequest && (
        <TenantIdDialog
          visible={tenantDialogVisible}
          onClose={handleTenantDialogClose}
        />
      )}
    </div>
  );
};

// Styling for the page
const styles = {
  container: (isMobile) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "100dvh",
    width: "100%",
    minWidth: "100vw",
    padding: isMobile ? "20px 12px" : "32px 20px",
    boxSizing: "border-box",
  }),
  card: (isMobile, isTablet) => ({
    width: "100%",
    maxWidth: isMobile ? "100%" : "550px",
    minWidth: isMobile ? "100%" : "550px",
    padding: 0,
    boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
    borderRadius: "16px",
    backgroundColor: "#fff",
    overflow: "hidden",
  }),
  cardContent: (isMobile, isTablet) => ({
    padding: isMobile ? "24px 16px" : isTablet ? "36px 28px" : "48px 40px",
  }),
  header: (isMobile) => ({
    textAlign: "center",
    marginBottom: isMobile ? "24px" : "40px",
  }),
  logo: (isMobile) => ({
    display: "block",
    margin: "0 auto 16px auto",
    height: isMobile ? 56 : 80,
    width: "auto",
    maxWidth: "100%",
  }),
  title: (isMobile) => ({
    margin: "0 0 8px 0",
    color: "#1a1a1a",
    fontWeight: "600",
    fontSize: isMobile ? "24px" : "28px",
    lineHeight: isMobile ? "30px" : "36px",
  }),
  subtitle: (isMobile) => ({
    color: "#666",
    fontSize: isMobile ? "14px" : "16px",
    margin: 0,
    display: "block",
  }),
  tenantText: (isMobile) => ({
    color: "#1f4f8f",
    fontSize: isMobile ? "13px" : "15px",
    fontWeight: 600,
    margin: 0,
    overflowWrap: "anywhere",
  }),
  tenantRow: (isMobile) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    marginTop: "8px",
    backgroundColor: "#e6f0ff",
    padding: isMobile ? "4px 10px" : "4px 12px",
    borderRadius: "12px",
    maxWidth: "100%",
    width: "fit-content",
    marginLeft: "auto",
    marginRight: "auto",
  }),
  tenantIcon: {
    color: "#1f4f8f",
    fontSize: "16px",
  },
  form: {
    width: "100%",
  },
  formItem: {
    marginBottom: "24px",
  },
  submitFormItem: {
    marginBottom: 0,
    marginTop: "32px",
  },
  keepSignedInItem: {
    marginBottom: "16px",
  },
  keepSignedInCheckbox: (isMobile) => ({
    color: "#333",
    fontSize: isMobile ? "13px" : "14px",
  }),
  label: (isMobile) => ({
    fontWeight: "600",
    color: "#333",
    fontSize: isMobile ? "13px" : "14px",
  }),
  input: (isMobile) => ({
    borderRadius: "8px",
    border: "1px solid #d9d9d9",
    fontSize: isMobile ? "15px" : "16px",
    transition: "all 0.3s ease",
    height: isMobile ? "44px" : "48px",
    width: "100%",
  }),
  inputIcon: {
    color: "#999",
  },
  button: (isMobile) => ({
    width: "100%",
    backgroundColor: "#1890ff",
    borderColor: "#1890ff",
    fontWeight: "600",
    fontSize: isMobile ? "15px" : "16px",
    height: isMobile ? "44px" : "48px",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(24, 144, 255, 0.3)",
    transition: "all 0.3s ease",
  }),
};

export default LoginComponent;
